import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import BusinessCustomersActions from "../../../Redux/transition/customers/business-customer.reducer";
import CustomToast from '../../../Components/CustomToast';
import { useTranslation, Trans } from "react-i18next";
import "../../../Styles/components/tags.scss";
import { MultiSelect } from 'primereact/multiselect';

const resolveTagId = (selected) => {
    const item = Array.isArray(selected) ? selected[0] : selected;
    if (item == null) return null;
    return typeof item === 'object' ? (item.value ?? item.id) : item;
};

export default function RemoveSubscribersDialog({ refreshCustomers, visible, onHide, selectedCustomers, setSelectedCustomers, specificTagClicked, removeDialogClick,
    rowSubClicked }) {

    const { t } = useTranslation("common");
    const [selectedTag, setSelectedTag] = useState([]);
    const [dissociateToastTrigger, setDissociateToastTrigger] = useState(false);
    const [clickRemove, setClickRemove] = useState(false);
    const [dissociateToast, setDissociateToast] = useState({
        severity: "",
        summary: "",
        detail: "",
        life: 0
    });
    const [bulkDissociateToastTrigger, setBulkDissociateToastTrigger] = useState(false);
    const [clickBulkRemove, setClickBulkRemove] = useState(false);
    const [bulkDissociateToast, setBulkDissociateToast] = useState({
        severity: "",
        summary: "",
        detail: "",
        life: 0
    });

    //Dispatch Actions
    const dispatch = useDispatch();
    const getTags = useCallback((businessId) => dispatch(BusinessCustomersActions.tagsRequest(businessId)), [dispatch]);
    const dissociateSubscriberFromTag = useCallback((tagId, bcId) => { dispatch(BusinessCustomersActions.dissociateSubscriberFromTagRequest(tagId, bcId)) }, [dispatch]);
    const bulkDissociateSubscribersFromTag = useCallback((tagId, bcIds) => { dispatch(BusinessCustomersActions.bulkDissociateSubscribersFromTagRequest(tagId, bcIds)) }, [dispatch]);

    //Redux State from Store
    const defMembership = useSelector((state) => state.defMembership.defMembership);
    const { dissociatingTag, dissociatedTag, bulkDissociatingTag, bulkDissociatedTag, tagGroupsGraphed } = useSelector((state) => state.businessCustomers);

    useEffect(() => {
        if (!visible) {
            setSelectedTag([]);
            setClickRemove(false);
            setClickBulkRemove(false);
        }
    }, [visible]);

    useEffect(() => {

        if (!dissociatingTag && clickRemove && dissociatedTag) {
            showDissociateToast("success", t("tags.success"), t("tags.tag_remove_detail_success"))
        }
        if (!dissociatingTag && clickRemove && !dissociatedTag) {
            showDissociateToast("error", t("tags.error_summary"), t("tags.remove_detail_error"))
        }
        if (!bulkDissociatingTag && clickBulkRemove && bulkDissociatedTag) {
            showBulkDissociateToast("success", t("tags.success"), t("tags.tag_remove_detail_success"))
        }
        if (!bulkDissociatingTag && clickBulkRemove && !bulkDissociatedTag) {
            showBulkDissociateToast("error", t("tags.error_summary"), t("tags.remove_detail_error"))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dissociatingTag, clickRemove, dissociatedTag, bulkDissociatingTag, clickBulkRemove, bulkDissociatedTag]);

    const showDissociateToast = (severity, summary, detail) => {
        setDissociateToast({
            severity,
            summary,
            detail,
            life: 4000,
        });
        setDissociateToastTrigger(true);
    }
    const showBulkDissociateToast = (severity, summary, detail) => {
        setBulkDissociateToast({
            severity,
            summary,
            detail,
            life: 4000,
        });
        setBulkDissociateToastTrigger(true);
    }

    const handleDissociate = () => {
        const tagId = specificTagClicked?.id ?? resolveTagId(selectedTag);
        if (rowSubClicked) {
            setClickRemove(true)
            const singleCustomerId = rowSubClicked.businessCustomer?.id ?? rowSubClicked.id;
            dissociateSubscriberFromTag(tagId, singleCustomerId);
        } else {
            setClickBulkRemove(true);
            const bcIds = selectedCustomers.map(sub =>
                typeof sub === "object" ? (sub.businessCustomer?.id ?? sub.id) : sub
            );
            bulkDissociateSubscribersFromTag(tagId, bcIds);
        }
    }

    const customerName = rowSubClicked?.businessCustomer?.name;
    const selectedTagForHint = specificTagClicked || (Array.isArray(selectedTag) ? selectedTag[0] : selectedTag);
    const selectedTagName = selectedTagForHint?.name || selectedTagForHint?.label;

    const baseStyle = { fontWeight: "bold", fontSize: "14px" };
    const getStyle = (color) => ({ ...baseStyle, color });

    const handleSelectionChange = (e) => {
        const incoming = Array.isArray(e?.value) ? e.value : [];
        if (incoming.length === 0) {
            setSelectedTag([]);
            return;
        }
        const newSelection = incoming[incoming.length - 1];
        setSelectedTag([newSelection]);
    };

    const customerTagOptions = useMemo(() => {
        return (rowSubClicked?.tags || [])
            .filter((tag) => tag?.id && tag?.name)
            .map((tag) => ({ label: tag.name, value: tag.id, name: tag.name, id: tag.id }));
    }, [rowSubClicked]);

    const groupedOptions = useMemo(() => {
        let availableOptions = tagGroupsGraphed;

        if (selectedCustomers.length > 1) {
            const currentOptions = selectedCustomers.flatMap(i => i?.tags);
            const currentTagIds = new Set(currentOptions.map(t => t?.id));
            const newTagGroups = {
                ...tagGroupsGraphed,
                groups: tagGroupsGraphed?.groups
                    .map(group => {
                        const filteredTags = group.tags.filter(tag => currentTagIds.has(tag.id));
                        return {
                            ...group,
                            tags: filteredTags
                        };
                    })
                    .filter(group => group.tags.length > 0)
            };
            availableOptions = newTagGroups ? newTagGroups : tagGroupsGraphed;
        }
        return availableOptions?.groups?.filter(group => group && group.name).map(group => ({
            label: group?.name,
            items: (group.tags || []).filter(tag => tag && tag?.name && tag?.id).map(tag => ({
                label: tag?.name, value: tag?.id
            }))
        }))
            .filter(group => group.items.length > 0)
    }, [tagGroupsGraphed, selectedCustomers]);

    const showSpecificTagConfirm = !!(rowSubClicked && specificTagClicked);
    const showCustomerTagPicker = !!(rowSubClicked && !specificTagClicked);
    const canSubmitSingle = showSpecificTagConfirm || (showCustomerTagPicker && selectedTag?.length > 0);

    return (
        <Dialog
            header={t("tags.remove_dialog_header")}
            visible={visible}
            style={{ width: '350px' }}
            onHide={() => {
                setSelectedTag([]);
                onHide();
            }}
        >
            <CustomToast
                {...dissociateToast}
                trigger={dissociateToastTrigger}
                handleActionsAfterToast={() => {
                    setDissociateToastTrigger(false);
                    setClickRemove(false);
                    onHide();
                    refreshCustomers();
                    getTags(defMembership.business.id);
                    setSelectedCustomers([]);
                    setSelectedTag([]);
                }}
                handleActionsAfterErrorToast={() => {
                    setDissociateToastTrigger(false);
                    onHide();
                    setClickRemove(false);
                    refreshCustomers();
                }}
            />

            <CustomToast
                {...bulkDissociateToast}
                trigger={bulkDissociateToastTrigger}
                handleActionsAfterToast={() => {
                    setBulkDissociateToastTrigger(false);
                    setClickBulkRemove(false);
                    onHide();
                    refreshCustomers();
                    getTags(defMembership.business.id);
                    setSelectedCustomers([]);
                    setSelectedTag([]);
                }}
                handleActionsAfterErrorToast={() => {
                    setDissociateToastTrigger(false);
                    onHide();
                    setClickRemove(false);
                }}
            />

            <div className="p-fluid remove-main-div">

                {showSpecificTagConfirm ? <div className='mt-3'>
                    <p>
                        <Trans
                            i18nKey={"tags.remove-hint"}
                            values={{ customerName: customerName, tagName: specificTagClicked?.name }}
                            components={{
                                1: <span style={getStyle("#000000")} />,
                                2: <span style={getStyle("#9747FF")} />
                            }}
                        >
                        </Trans>
                        <hr style={{ borderTop: "2px solid #e0e0e0" }} className='mt-2 mb-2' />
                    </p>
                </div> :
                    <div>
                        {showCustomerTagPicker ? (
                            <div className='mt-3 mb-2'>
                                <p>
                                    <Trans
                                        i18nKey={"tags.remove-hint"}
                                        values={{
                                            customerName: customerName,
                                            tagName: selectedTagName || t("tags.select_tag"),
                                        }}
                                        components={{
                                            1: <span style={getStyle("#000000")} />,
                                            2: <span style={getStyle("#9747FF")} />
                                        }}
                                    >
                                    </Trans>
                                    <hr style={{ borderTop: "2px solid #e0e0e0" }} className='mt-2 mb-2' />
                                </p>
                            </div>
                        ) : null}
                        <div className='mb-1'> <label htmlFor="tagLabel" className='tag_label'>{t("tags.choose_tag")}</label></div>
                        <MultiSelect
                            className="tag_select custom-multiselect"
                            panelClassName="tag-multiselect-panel"
                            id="tag_select_id"
                            value={selectedTag}
                            onChange={(e) => handleSelectionChange(e)}
                            options={showCustomerTagPicker ? customerTagOptions : groupedOptions}
                            showSelectAll={false}
                            optionLabel="label"
                            {...(showCustomerTagPicker
                                ? {}
                                : {
                                    optionGroupLabel: "label",
                                    optionGroupChildren: "items",
                                })}
                            placeholder={t("tags.select_tag")}
                            display="chip" />
                    </div>}

                <div className="flex justify-content-end align-items-center mt-4 gap-3" >
                    <Button label={t('subscriber.cancel')}
                        icon="pi pi-times"
                        onClick={() => {
                            setSelectedTag([]);
                            onHide();
                        }}
                        className="p-button-text"
                        id="tag_cancel_btns"
                    />
                    <Button
                        label={t("tags.remove_btn_text")}
                        id='tag_remove_btn'
                        onClick={() => {
                            handleDissociate();
                        }}
                        disabled={rowSubClicked
                            ? dissociatingTag || clickRemove || !canSubmitSingle
                            : dissociatingTag || clickRemove || bulkDissociatingTag || clickBulkRemove || !selectedTag?.length}
                        loading={bulkDissociatingTag || dissociatingTag}
                    />
                </div>
            </div>
        </Dialog>
    );
}
